import { Button, Flex, IconWarning, Select, Spinner } from '@gmg/gmg-react-components';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AppContext, { Role } from 'src/AppContext';
import { COLORCARD, CPGO, ModuleId, SettingsModuleId } from 'src/graphql/mappers/module';
import { UserDetails } from 'src/graphql/useGetUserDetails';
import { isGmgPartnerRole } from 'src/role';
import { getEditUserEvent, getInviteUserEvent } from 'src/tracking';
import styled from 'styled-components';
import FormElement from '../FormElement';
import FormElementWrapper, { Options } from '../SelectElementWrapper';
import { CPGORole, DEFAULT_ROLE_SELECTION } from './ColorProofGo/cpGo';
import { SingleModule } from './ModuleSelector';
import ProductsSection from './ProductsSection';
import { AddOrEditUserSubmitData } from './UserManagement';
import { LicenseSystemAccessRole } from 'src/graphql/generated/types';


export type CPGORolSelection = Omit<Options, 'value'> & { value: CPGORole } | undefined;
export interface AddOrEditUserModalForm {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: Omit<Options, 'value'> & { value: Role };
    userGroupSelection: Array<Options>;
    cpGoAssignedLicenseSystems: Array<Options> | undefined;
    cpGoRoleSelection: Omit<Options, 'value'> & { value: CPGORole } | undefined;
    colorCardAssignedLicenseSystems: Array<Options> | undefined;
}
export interface UserModalState {
    isLoading: boolean;
    errMsg: string | undefined;
}

export interface AddOrEditUserModalProps {
    onClose: () => void;
    initialValues?: UserDetails;
    assignableRoles: Array<Role>;
    availableGroups: Map<string, string>;
    selectableModules: Array<SingleModule>;
    onHandleEditUser?: (
        userData: AddOrEditUserSubmitData,
        setModalState: (props: UserModalState) => void,
        onClose: () => void,
    ) => void;
    onHandleInviteUser?: (
        userData: AddOrEditUserSubmitData,
        setModalState: (props: UserModalState) => void,
        onClose: () => void,
    ) => void;
    onShowSettingsModal: (moduleId: SettingsModuleId) => void;
    selectedModuleIds: Array<ModuleId>;
    onChangeModuleSelection: (clickedModule: ModuleId) => void;
    deselectedModuleIds: Set<ModuleId>;
}

const AddOrEditUserModal: FC<AddOrEditUserModalProps> = (props) => {
    const useCase: 'new' | 'edit' = props.initialValues ? 'edit' : 'new';
    const FIXED_COLORCARD_ROLE = LicenseSystemAccessRole.Operator;

    const { t } = useTranslation();
    const { trackEvent } = useContext(AppContext);
    const [modalState, setModalState] = useState<UserModalState>({ isLoading: false, errMsg: undefined });
    const { register, handleSubmit, control, setValue, formState: { errors } } = useFormContext<AddOrEditUserModalForm>();

    const registerOptionsForName = {
        required: t('Validation.forms.field_required_val', 'Please fill out this field.'),
        maxLength: {
            value: 50,
            message: t('Validation.forms.maxLength_exceeded_val', {
                defaultValue: 'Oops, you have exceeded the limit of {{maxCharacters}} characters.',
                maxCharacters: 50,
            }),
        },
        setValueAs: (value: string) => value.trim(),
    };

    const emailRegex = /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[a-zA-ZäöüÄÖÜ0-9-]+\.)+[a-zA-Z]{2,}$/;
    const registerOptionsForEmail =
    {
        required: t('Validation.forms.field_required_val', 'Uh oh, please fill out this field.'),
        maxLength: {
            value: 50,
            message: t('Validation.forms.maxLength_exceeded_val', {
                defaultValue: 'Oops, you have exceeded the limit of {{maxCharacters}} characters.',
                maxCharacters: 50,
            }),
        },
        validate: {
            isValid: (value: string) => emailRegex.test(value.trim())
                ? true
                : t('Validation.forms.invalidEmail_val', 'Oh no, it seems like your email address is invalid. Please try again.'),
        },
    };

    // t('Roles.GMGAdmin_label', 'GMG Admin')
    // t('Roles.GMGSuperUser_label', 'GMG Super User')
    // t('Roles.OrganizationGroupAdmin_label', 'Organization Group Admin')
    // t('Roles.OrganizationAdmin_label', 'Organization Admin')
    // t('Roles.Member_label', 'Member')
    // t('Roles.GMGPartner_label', 'GMG Partner')
    // Once a user is a GMG Partner the role select is read-only.
    const currentRole = props.initialValues?.user.role;
    const roleOptions = props.assignableRoles
        .map(role => ({ value: role, label: t(`Roles.${role}_label`, role) }));

    // set initial values for the modal if any in edit useCase
    useEffect(() => {
        if (useCase === 'new') return;
        const activeRole = roleOptions.find(({ value }) => value === currentRole)!;
        setValue('role', activeRole);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let onHandleAddOrEditUser: (
        userData: AddOrEditUserSubmitData,
        setModalState: (props: UserModalState) => void,
        onClose: () => void,
    ) => void;
    if (useCase === 'new') { onHandleAddOrEditUser = props.onHandleInviteUser!; }
    if (useCase === 'edit') { onHandleAddOrEditUser = props.onHandleEditUser!; }

    const hasCpGoBeenDeselected = props.deselectedModuleIds.has(CPGO.id);
    const hasColorCardBeenDeselected = props.deselectedModuleIds.has(COLORCARD.id);

    return (
        <Container>
            <form onSubmit={handleSubmit((data) => {
                const formData: AddOrEditUserSubmitData = {
                    id: props.initialValues?.user.id,
                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    modules: props.selectedModuleIds,
                    role: data.role.value,
                    // Note: User groups were used for folder access in Optimize.
                    // We decided not to use user groups anymore. Instead, a user has access to all folders within an organization.
                    // Existing user group data is ignored.
                    // When a user with assigned user groups is edited, those groups will be removed from the user as groups have no functionality anymore.
                    groups: [],
                    cpGo: hasCpGoBeenDeselected
                        // in this useCase ( disabled CpGo checkbox ) role does not matter we remove all assignments
                        ? { assignedLicenseSystems: [], role: DEFAULT_ROLE_SELECTION.value }
                        : {
                            assignedLicenseSystems: data.cpGoAssignedLicenseSystems?.map(ls => ls.value) || undefined,
                            role: data?.cpGoRoleSelection?.value || undefined,
                        },
                    colorCard: hasColorCardBeenDeselected
                        ? { assignedLicenseSystems: [], role: FIXED_COLORCARD_ROLE } // in this useCase ( disabled ColorCard checkbox ) we remove all assignments
                        : {
                            assignedLicenseSystems: data.colorCardAssignedLicenseSystems?.map(ls => ls.value) || undefined,
                            role: data?.colorCardAssignedLicenseSystems ? FIXED_COLORCARD_ROLE : undefined, // ColorCard users must always be Operators
                        },
                };

                if (useCase === 'edit') trackEvent(getEditUserEvent(formData.role, formData.modules));
                if (useCase === 'new') trackEvent(getInviteUserEvent());
                onHandleAddOrEditUser(formData, setModalState, props.onClose);
            })}
            >
                <FormElement
                    label={t('AddOrEditUserModal.email_title', 'User email address')}
                    registerElement={{ ...register('email', registerOptionsForEmail) }}
                    dataProperty='email'
                    placeholder={t('AddOrEditUserModal.input_email_desc', 'Enter email address')}
                    validationError={errors?.email?.message?.toString() ?? modalState.errMsg}
                    defaultValue={props.initialValues?.user.email}
                    readonly={!!props.initialValues}
                />

                <FormElement
                    label={t('UpdateUserNameModalContainer.first_name_title', 'First name')}
                    registerElement={{ ...register('firstName', registerOptionsForName) }}
                    dataProperty="firstName"
                    placeholder={t('UpdateUserNameModalContainer.input_firstname_desc', 'Enter first name')}
                    validationError={errors.firstName?.message}
                    defaultValue={props.initialValues?.user.firstName ?? ''}
                />
                <FormElement
                    label={t('UpdateUserNameModalContainer.family_name_title', 'Last name')}
                    registerElement={{ ...register('lastName', registerOptionsForName) }}
                    dataProperty="lastName"
                    placeholder={t('UpdateUserNameModalContainer.input_lastname_desc', 'Enter last name')}
                    validationError={errors.lastName?.message}
                    defaultValue={props.initialValues?.user.lastName ?? ''}
                />

                <Controller
                    control={control}
                    name="role"
                    rules={{ required: true }}
                    render={({
                        field: { onChange, ref, value },
                    }) => (
                        <FormElementWrapper
                            label={t('AddOrEditUserModal.role_title', 'User role')}
                            height="35px"
                        >
                            <Select
                                onChange={onChange}
                                ref={ref}
                                value={value}
                                options={roleOptions}
                                isDisabled={isGmgPartnerRole(currentRole)}
                                placeholder={t('AddOrEditUserModal.input_role_desc', 'Select a user role')}
                                name={'Role Controller'}
                                width="100%"
                                error={errors.role && t('AddOrEditUserModal.role_error_desc', 'Uh oh, please select a user role.')}
                            />
                        </FormElementWrapper>
                    )}
                />

                <ProductsSection
                    sectionTitle={t('AddOrEditUserModal.modules_title', 'Products')}
                    selectableModules={props.selectableModules}
                    onHandleModuleChange={props.onChangeModuleSelection}
                    onSettingsIconClick={props.onShowSettingsModal}
                    selectedModuleIds={props.selectedModuleIds}
                />

                < SpinnerWrapper className='px10_400_weak'>
                    {modalState.isLoading &&
                        <Spinner
                            svgSize="16px"
                            labelPosition='right'
                            label={t('AddOrEditUserModal.spinner_desc', 'Validating user email...')}
                        />
                    }
                </SpinnerWrapper>
                <Flex width='100%' alignItems='center'>
                    {(hasCpGoBeenDeselected || hasColorCardBeenDeselected) &&
                        <Flex gap='8px' alignItems='center'>
                            <StyledWarningIcon />
                            <span className='px10_400_normal'>
                                {t('AddOrEditUserModal.deselected_module_warning_msg', 'All license system pairings to this product will be removed for this user')}
                            </span>
                        </Flex>
                    }
                    <Flex gap='16px' ml='auto' minWidth='fit-content'>
                        <Button
                            variant="primary"
                            type="submit"
                            data-testid="invite-user-btn"
                            // As we don't close the button on submit, we need to disable it.
                            // Otherwise, it will be possible to click the button multiple times, resulting in multiple users being added.
                            disabled={modalState.isLoading}
                        >
                            {
                                useCase === 'new'
                                    /* Different fallback text as English string, as otherwise, the unit tests will have a conflict with the other button. */
                                    ? t('AddUserBtn.button_title', 'Add user')
                                    : t('ButtonTitles.action_apply_btn', 'Apply')
                            }

                        </Button>
                        <Button
                            variant="secondary"
                            type="reset"
                            onClick={props.onClose}
                        >{t('ButtonTitles.action_cancel_btn', 'Cancel')}
                        </Button>
                    </Flex>
                </Flex>
            </form>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    form {
        width: 100%;
    }
`;
const SpinnerWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    height: 16px;
    padding-left: 25px;
`;

const StyledWarningIcon = styled(IconWarning)`
    width: 16px;
    height: 16px;
    color: ${props => props.theme.colors.warning};
`;

export default AddOrEditUserModal;
